import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ViewContainer, Title } from './Activity.styles';
import { LOG } from '../../common/Utilities';
import ActivityFilter from './components/ActivityFilter';
import ActivityTable from './components/ActivityTable';

// Interface matching container's data structure
interface ActivityItem {
  id: string;
  name: string;
  team: string;
  firstContactAfter9AM: string | null;
  thirdContactAfter9AM: string | null;
  lastBefore3pm: string | null;
  firstAfter4pm: string | null;
  thirdToLastPin: string | null;
  lastPin: string | null;
  pinsDropped: number | string;
  sales: number | string | null;
  notHomeCount: number | string | null;
  notInterestedCount: number | string | null;
  gavePitchCount: number | string | null;
  callBacksCount: number | string | null;
  percentNotInterested: number | string | null;
  percentGavePitch: number | string | null;
  percentNotHome: number | string | null;
  percentCallBack: number | string | null;
  knocksToSales: number | string | null;
  notInterestedToSales: number | string | null;
  gavePitchToSales: number | string | null;
}

// Define a type for sort order
type Order = 'asc' | 'desc';

// Define a type for sortable property keys
type SortableKey = keyof ActivityItem;

// Interface for userActivityKpis
interface UserActivityKpis {
  notInterested: { id: string };
  knocks: { id: string };
  callBack: { id: string };
  interested: { id: string };
  gavePitch: { id: string };
  sales: { id: string };
  notHome: { id: string };
  [key: string]: { id: string };
}

interface ActivityViewProps {
  loading: boolean;
  activityData: ActivityItem[];
  error: string | null;
  refreshData: (
    teamId?: string,
    date?: string,
    teamName?: string
  ) => Promise<void>;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defaultTeamId: string;
  allowAllTeamsOption: boolean;
  isManager: boolean;
  userActivityKpis?: UserActivityKpis;
}

// Utility function to safely access the userActivityKpis with retry mechanism
const getUserActivityKpis = (
  retryCount = 0,
  maxRetries = 5
): UserActivityKpis | undefined => {
  try {
    // Check if running in browser environment
    // @ts-ignore
    const settings = global.appSettings;

    // @ts-ignore
    if (!settings?.userActivityKpis) {
      // Only log the error on the final attempt to avoid console spam
      if (retryCount >= maxRetries) {
        LOG.error(
          'View - No userActivityKpis found in global.appSettings after maximum retries'
        );
        return undefined;
      }
      return undefined;
    }

    // @ts-ignore
    const kpis = settings.userActivityKpis;

    return kpis;
  } catch (error) {
    LOG.error('Error accessing userActivityKpis:', error);
    return undefined;
  }
};

// Fetch default KPIs - will be undefined initially if data isn't ready
const defaultKpis = getUserActivityKpis();

// Helper function to get sortable value from any type
const getSortableValue = (value: any): string | number => {
  // Special handling for null/undefined/empty values
  // Using -Infinity ensures these values appear at the bottom when sorting in descending order
  // and at the top when sorting in ascending order
  if (value === null || value === undefined || value === '--') {
    return -Infinity;
  }

  // Handle percentage strings by extracting the numeric portion
  if (typeof value === 'string' && value.endsWith('%')) {
    return parseFloat(value.replace('%', ''));
  }

  // Handle time values (e.g., "9:05 AM") by converting to Date objects for proper comparison
  if (
    typeof value === 'string' &&
    /\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?/i.test(value)
  ) {
    try {
      // Create a date object with the time part for comparison
      const today = new Date().toLocaleDateString('en-CA'); // Get just the date portion
      const dateTimeStr = `${today} ${value}`;
      return new Date(dateTimeStr).getTime();
    } catch (e) {
      // If parsing fails, fall back to string comparison
      return value;
    }
  }

  // For pins dropped and other numeric string values, convert to actual numbers for proper ordering
  if (typeof value === 'string' && !isNaN(Number(value))) {
    return Number(value);
  }

  // Default handling for any other value types
  return value;
};

// Comparison function for sorting
const compareValues = (
  a: any,
  b: any,
  orderBy: SortableKey,
  order: Order
): number => {
  const valueA = getSortableValue(a[orderBy]);
  const valueB = getSortableValue(b[orderBy]);

  // Special handling for empty values - always put them at the bottom regardless of sort direction
  if (valueA === -Infinity && valueB !== -Infinity) {
    return 1; // A is empty, B is not - A goes after B
  }
  if (valueA !== -Infinity && valueB === -Infinity) {
    return -1; // A is not empty, B is empty - A goes before B
  }
  if (valueA === -Infinity && valueB === -Infinity) {
    return 0; // Both are empty - no change in order
  }

  // Normal comparison logic
  if (valueA < valueB) {
    return order === 'asc' ? -1 : 1;
  }
  if (valueA > valueB) {
    return order === 'asc' ? 1 : -1;
  }
  return 0;
};

// Function to sort data
const sortData = (
  data: ActivityItem[],
  orderBy: SortableKey,
  order: Order
): ActivityItem[] => {
  return [...data].sort((a, b) => compareValues(a, b, orderBy, order));
};

const ActivityView: React.FC<ActivityViewProps> = ({
  loading,
  activityData,
  error,
  refreshData,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  totalCount,
  defaultTeamId,
  allowAllTeamsOption,
  isManager,
  userActivityKpis: userActivityKpisProp,
}) => {
  // State for filters
  const [selectedTeam, setSelectedTeam] = useState<string>('All Teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string>(defaultTeamId);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    // Get the current local date in YYYY-MM-DD format
    const now = new Date();
    return now.toLocaleDateString('en-CA'); // en-CA uses YYYY-MM-DD format
  });
  const [dateError, setDateError] = useState<string | null>(null);

  // States for applied filters
  const [appliedTeamId, setAppliedTeamId] = useState<string>(defaultTeamId);
  const [appliedDate, setAppliedDate] = useState<string>(() => {
    // Get the current local date in YYYY-MM-DD format
    const now = new Date();
    return now.toLocaleDateString('en-CA'); // en-CA uses YYYY-MM-DD format
  });
  const [appliedTeamName, setAppliedTeamName] = useState<string>('All Teams');

  // State to force re-render of the picker
  const [updatePickerKey, setUpdatePickerKey] = useState<number>(0);

  // Use KPIs from props (container is the single source of truth)
  // Fall back to global.appSettings if prop not provided
  const [userActivityKpis, setUserActivityKpis] = useState<
    UserActivityKpis | undefined
  >(userActivityKpisProp || defaultKpis);

  // Add state for sorting
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortableKey>('pinsDropped');

  // Sync KPIs from props when they become available
  useEffect(() => {
    if (userActivityKpisProp) {
      setUserActivityKpis(userActivityKpisProp);
    }
  }, [userActivityKpisProp]);

  // Handle team selection - combined function to ensure consistency
  const handleTeamSelect = (id: string, name: string) => {
    // Check if we received the "all" ID from the picker
    const effectiveId = id === 'all' ? 'all' : id;
    const effectiveName = name || 'All Teams';

    setSelectedTeamId(effectiveId);
    setSelectedTeam(effectiveName);

    // Only apply filters if there's no date error
    if (!dateError) {
      setAppliedTeamId(effectiveId);
      setAppliedTeamName(effectiveName);
      refreshData(effectiveId, selectedDate, effectiveName);
    }

    // Force a refresh of the picker to ensure it shows the correct selection
    setUpdatePickerKey((prev) => prev + 1);
  };

  // Handle date selection
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);

    // Check if selected date is in the future
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    const selectedDateObj = new Date(newDate);

    if (selectedDateObj > today) {
      setDateError(
        "You've selected a date in the future. Please select today or a past date."
      );
    } else {
      setDateError(null);

      // Apply the filters since date is valid
      setAppliedDate(newDate);
      refreshData(selectedTeamId, newDate, selectedTeam);
    }
  };

  // Handle sort request
  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Apply sorting to the entire dataset first
  const sortedData = sortData(activityData, orderBy, order);

  // Set up a second useEffect to log when the component mounts and initialize values properly
  useEffect(() => {
    // Make sure state is properly initialized
    setSelectedTeamId(defaultTeamId);
    setAppliedTeamId(defaultTeamId);
  }, [defaultTeamId]);

  // Log whenever a refresh happens
  const handleRefreshClick = () => {
    refreshData(appliedTeamId, appliedDate, appliedTeamName);
  };

  return (
    <ViewContainer>
      <Box sx={{ mb: 3 }}>
        <Title>Team/User Activity</Title>
      </Box>

      {/* Activity Filter Component */}
      <ActivityFilter
        selectedTeamId={selectedTeamId}
        selectedDate={selectedDate}
        dateError={dateError}
        handleTeamSelect={handleTeamSelect}
        handleDateChange={handleDateChange}
        updatePickerKey={updatePickerKey}
        loading={loading}
        onRefreshClick={handleRefreshClick}
        isManager={isManager}
        allowAllTeamsOption={allowAllTeamsOption}
      />

      {/* Activity Table Component */}
      <ActivityTable
        loading={loading}
        activityData={sortedData}
        error={error}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        order={order}
        orderBy={orderBy}
        handleRequestSort={handleRequestSort}
        userActivityKpis={userActivityKpis}
      />
    </ViewContainer>
  );
};

export default ActivityView;
